import { useState, useCallback, useRef, type FocusEvent } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { ulid } from 'ulid';
import Input from 'rsuite/Input';
import { UserRole } from '@/models/profile';
import { AssetRole } from '@/models/asset';
import { useAuthorization, profileSelector } from '@/hooks/useAuthorization.hook';
import { useVideoPreview } from '@/hooks/useVideoPreview.hook';
import Authorized from '@/components/Authorized';
import BackTo from '@/components/BackTo';
import VideoRecorder from '@/components/VideoRecorder';
import { VideoRecordController } from '@/upload/video-record-controller';
import { type RecorderOptions, type StageType } from '@/upload/upload-types';
import styles from './record.module.css';

const route = '/record/';

interface RecordSearchParams {
  back?: string;
  prev?: string;
  id?: string;
}

const generateTitle = (role?: UserRole): string => {
  if (role === UserRole.ORGANIZER) {
    return "Record your competition video";
  }
  return "Record your jump video";
}

const recordSearchValidate = (search: Record<string, unknown>): RecordSearchParams => {
  // const role: UserRole = (search.role as UserRole) ?? UserRole.COMPETITOR;
  return {
    back: (search.back as string) ?? "Back",
    prev: (search.prev as string) ?? "/home",
    id: (search.id as string) ?? ulid(),

  }
}

export const Route = createFileRoute(route)({
  component: RecordPage,
  validateSearch: recordSearchValidate
})

function RecordPage() {
  const search = Route.useSearch();
  const profile = useAuthorization(profileSelector);
  const [title, setTitle] = useState("");
  const idRef = useRef(search.id);
  const { stopStream } = useVideoPreview();

  const recorderOptions = useCallback((title: string): RecorderOptions | undefined => {
    if (profile != null) {
      const assetRole = profile?.role === UserRole.COMPETITOR ? AssetRole.JUMP : AssetRole.COMPETITION;
      let jumpId, competitionId: string | undefined;
      if (assetRole === AssetRole.COMPETITION) {
        competitionId = idRef.current;
      } else {
        jumpId = idRef.current;
      }
      const options: RecorderOptions = {
        role: assetRole,
        jumpId: jumpId,
        competitionId: competitionId,
        email: profile.email,
        info: {
          name: title,
          type: VideoRecordController.mimeType
        },
        onStage: (_: StageType) => {} // for now
      };
      return options;
    }
  }, [profile]);

  const onBlur = (event: FocusEvent<HTMLInputElement>) => {
      const newTitle = event.target.value;
      setTitle(newTitle);
  }

  return (
    <Authorized>
      <BackTo label={search.back ?? "Back"} route={search.prev} beforeNavigate={stopStream} />
      <div className={styles.pageContainer}>
        <h2 className={styles.title}>{generateTitle(profile?.role)}</h2>
        <Input className={styles.videoTitle} value={title} onChange={setTitle} placeholder="Please enter title" onBlur={onBlur}/>
        <VideoRecorder recordOptions={recorderOptions(title)} />
      </div>
    </Authorized>
  );
}
